import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type { BuilderApplicationListResponse } from '@/lib/types'

interface BuilderApplicationListParams {
  page?: number
  per_page?: number
  status?: string
  type?: string
}

interface UpdateBuilderApplicationPayload {
  id: string
  status?: 'approved' | 'denied'
  admin_notes?: string
}

const BUILDER_APPLICATIONS_KEY = ['builder-applications'] as const

export function useBuilderApplications(
  params?: BuilderApplicationListParams,
): ReturnType<typeof useQuery<BuilderApplicationListResponse>> {
  return useQuery({
    queryKey: [...BUILDER_APPLICATIONS_KEY, params],
    queryFn: () =>
      apiClient.get<BuilderApplicationListResponse>(
        endpoints.builderApplications.list({
          page: params?.page,
          per_page: params?.per_page,
          status: params?.status,
          type: params?.type,
        }),
      ),
  })
}

export function useUpdateBuilderApplication(): ReturnType<
  typeof useMutation<unknown, unknown, UpdateBuilderApplicationPayload>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateBuilderApplicationPayload) =>
      apiClient.patch(endpoints.builderApplications.update(id), body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BUILDER_APPLICATIONS_KEY })
    },
  })
}
